import { BrowserSession, initializeSdk, UcsSdk } from '@brandfolder/ucs-sdk-browser';
import React, { FC, useEffect, useState } from 'react';

import { UppyUcsUploader } from './UppyUCSUploader';
import fetchJson from '@api/api_helper';
import { COMPANION_SANDBOX_URL } from '@helpers/uppy';
import { UppyUploaderProps } from '../types/UppyTypes';

const originalFetch = window.fetch;

/**
 * Fetches a browser session for UCS uploads from the Boulder API.
 *
 * @param {Object} options - Configuration options
 * @param {string} options.bearerToken - Bearer token for authentication
 * @param {string} options.url - API endpoint URL
 * @param {boolean} [options.isGuestUpload] - Whether this is a guest upload
 * @param {string} [options.uploadKey] - Upload key for guest uploads
 * @returns {Promise<BrowserSession>} Browser session object
 */

const fetchBoulderUcsBrowserSession = async (options: {
  bearerToken: string;
  url: string;
  isGuestUpload?: boolean;
  uploadKey?: string;
}): Promise<BrowserSession> => {

  try {
    // Determine the request body based on whether it's a guest upload
    const body = options.isGuestUpload ? {
      upload_key: options.uploadKey,
      bf_token: BFG.BF_Token
    } : {
      brandfolder_key: BFG.brandfolder_key,
    };

    const response = await fetchJson(options.url, {
      method: 'POST',
      queuePriorityHigh: false,
      body,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${options.bearerToken}`,
      },
    });
    if (!response) {
      throw new Error(`Failed to create browser session: ${response}`);
    }

    return response;
  } catch (error) {
    console.error(error);
  }
};

export const UppyTusWrapper: FC<UppyUploaderProps> = (uppyProps) => {
  const [endpoint, setEndpoint] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [ucsSdkInstance, setUcsSdkInstance] = useState<UcsSdk>(null);
  const bearerToken = BFG.BF_Token


  useEffect(() => {
    const initializeTusEndpoint = async () => {
      try {
        if (!bearerToken) {
          throw new Error('Either bearerToken or isFl2 flag is required');
        }

        setIsLoading(true);
        setError(null);


        const ucsSdk = initializeSdk({
          createBrowserSessionFunction(): Promise<BrowserSession> {
            const isGuestUpload = uppyProps.guestUpload;
            const url = isGuestUpload
              ? `${BFG.domain}/api/v4/ucs/v2/guest_browser_session`
              : `${BFG.domain}/api/v4/ucs/v2/browser_session`;

            return fetchBoulderUcsBrowserSession({
              bearerToken,
              url,
              isGuestUpload,
              uploadKey: isGuestUpload ? uppyProps.uploadLinkKey : undefined,
            });
          }
        });

        let ucsTusUrl = '';
        try {
          ucsTusUrl = (await ucsSdk.getTusUrl()) || '';
          setUcsSdkInstance(ucsSdk);
          setEndpoint(ucsTusUrl);
        } catch (error) {
          console.error('Failed to get TUS URL:', error);
        }


      } catch (err) {
        console.error('Failed to initialize TUS endpoint:', err);
        setError(err instanceof Error ? err.message : 'Unknown error occurred');
      } finally {
        setIsLoading(false);
      }
    };

    initializeTusEndpoint();
  }, [bearerToken]);

    useEffect(() => {
    /**
     * Monkey-patching window.fetch to simulate onBeforeRequest behavior
     * for third-party uploads in Uppy since this feature is not supported natively.
     */
    const patchedFetch = (async (input: RequestInfo, init?: RequestInit): Promise<Response> => {
      const isCompanionRequest =
        typeof input === 'string' && input.includes(COMPANION_SANDBOX_URL);
      const isTusRequest = init?.body && typeof init?.body === "string";

      if (isCompanionRequest && isTusRequest) {
        try {
          const identityToken = await ucsSdkInstance.getIdentityToken();

          init.headers = {
            ...(init.headers || {}),
            Authorization: `Bearer ${identityToken}`
          };
        } catch (err) {
          console.error('Failed to fetch token:', err);
        }
      }

      return originalFetch(input, init);
    }) as typeof fetch;

    window.fetch = patchedFetch;

    return () => {
      window.fetch = originalFetch;
    }
  }, [ucsSdkInstance]);

  if (isLoading) {
    return <div>Loading uploader...</div>;
  }

  if (error) {
    return <div>Error initializing uploader: {error}</div>;
  }

  if (!endpoint) {
    return <div>Unable to initialize uploader endpoint</div>;
  }

  return <UppyUcsUploader {...uppyProps} endpoint={endpoint} ucsSdk={ucsSdkInstance} />;
};
